import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Typography, Tabs, Spin, Card, Row, Col } from "antd";
// TODO: Uncomment when API is ready
// import { Segmented } from "antd";
// import { ApiOutlined, CalculatorOutlined } from "@ant-design/icons";
import { ThunderboltOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
// import { apiGetOptionsData } from "api/apiPredictions"; // TODO: Uncomment when backend is ready
import mockData from "./mock_data.json";
import { generateManualOptionsData } from "./utils/manualDataGenerator";

// Hooks
import { useOptionCalculations } from "./hooks/useOptionCalculations";
import { useScenarioData } from "./hooks/useScenarioData";
import { useRiskAnalysis } from "./hooks/useRiskAnalysis";
import { useMatrixData } from "./hooks/useMatrixData";

// Components
import SymbolSelector from "./components/SymbolSelector";
import ManualInputForm from "./components/ManualInputForm";
import EmptyState from "./components/EmptyState";
import ControlsCard from "./components/ControlsCard";
import BestOptionAlert from "./components/BestOptionAlert";
import CompleteOverviewTab from "./components/CompleteOverviewTab";
import RobustRankingsTab from "./components/RobustRankingsTab";
import RiskAnalysisTab from "./components/RiskAnalysisTab";

const { Title } = Typography;

const OptionSimulator = () => {
  const { symbol: urlSymbol, expiration: urlExpiration } = useParams();
  const navigate = useNavigate();

  // TODO: Change back to "api" when backend is ready
  const [mode, setMode] = useState("manual"); // "api" or "manual"
  const [selectedSymbol, setSelectedSymbol] = useState(urlSymbol || null);
  const [optionsData, setOptionsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedExpiration, setSelectedExpiration] = useState(null);
  const [selectedStrike, setSelectedStrike] = useState(null);
  const [expectedMove, setExpectedMove] = useState(5);
  const [expectedDays, setExpectedDays] = useState(3);
  const [currentIV, setCurrentIV] = useState(null);
  const [futureIV, setFutureIV] = useState(null);
  const [calculationParams, setCalculationParams] = useState(null);

  const expirations = useMemo(
    () => (optionsData ? Object.keys(optionsData) : []),
    [optionsData]
  );

  const currentExpirationData = useMemo(
    () =>
      optionsData && selectedExpiration
        ? optionsData[selectedExpiration]
        : null,
    [optionsData, selectedExpiration]
  );

  const strikes = useMemo(() => {
    const allStrikes = currentExpirationData?.strikes || [];
    // Filter out strikes with entry price less than $0.30
    return allStrikes.filter((strike) => {
      const firstTradingDay = strike.trading_days[0];
      return firstTradingDay?.theoretical_price_now >= 0.3;
    });
  }, [currentExpirationData]);

  // Custom hooks for data calculations
  const { calculateBestOptions } = useOptionCalculations({
    strikes,
    expectedMove,
    expectedDays,
  });

  const currentStrikeData = useMemo(() => {
    if (!selectedStrike) return null;
    return strikes.find((s) => s.strike === selectedStrike);
  }, [strikes, selectedStrike]);

  const { strikeComparisonData } = useScenarioData({
    currentStrikeData,
    expectedDays,
    expectedMove,
    strikes,
  });

  const { riskAnalysisData, profitLossChartData } = useRiskAnalysis({
    currentStrikeData,
    expectedDays,
    expectedMove,
  });

  const {
    comprehensiveMatrixData,
    heatmapScale,
    allTradingDays,
    multiStrikeTimeDecayData,
  } = useMatrixData({ strikes, expectedMove });

  const bestOption = calculateBestOptions?.[0];

  // TODO: Uncomment when API is ready
  // Fetch options data from API
  /* eslint-disable no-unused-vars */
  const fetchOptionsData = useCallback(
    async (symbol) => {
      setLoading(true);
      try {
        // TODO: Replace with actual API call when backend is ready
        // const data = await apiGetOptionsData(symbol);

        // For now, return mock data
        const data = mockData;
        setOptionsData(data);

        // Set default expiration if available
        const availableExpirations = Object.keys(data);
        if (availableExpirations.length > 0) {
          // Use URL expiration if valid, otherwise use first available
          if (urlExpiration && availableExpirations.includes(urlExpiration)) {
            setSelectedExpiration(urlExpiration);
          } else {
            setSelectedExpiration(availableExpirations[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching options data:", error);
        setOptionsData(null);
      } finally {
        setLoading(false);
      }
    },
    [urlExpiration]
  );
  /* eslint-enable no-unused-vars */

  // Handle symbol change
  const handleSymbolChange = (symbol) => {
    setSelectedSymbol(symbol);
    setOptionsData(null);
    setSelectedExpiration(null);
    setSelectedStrike(null);

    if (symbol) {
      navigate(`/option-simulator/${symbol}`);
    } else {
      navigate("/option-simulator");
    }
  };

  // Handle expiration change with URL update
  const handleExpirationChange = (expiration) => {
    setSelectedExpiration(expiration);
    setSelectedStrike(null);

    if (mode === "api" && selectedSymbol && expiration) {
      navigate(`/option-simulator/${selectedSymbol}/${expiration}`);
    } else if (mode === "api" && selectedSymbol) {
      navigate(`/option-simulator/${selectedSymbol}`);
    }
  };

  // Handle manual calculation
  const handleManualCalculate = (params) => {
    setLoading(true);
    try {
      const data = generateManualOptionsData(params);
      setOptionsData(data);

      // Set default expiration
      const availableExpirations = Object.keys(data);
      if (availableExpirations.length > 0) {
        setSelectedExpiration(availableExpirations[0]);
      }

      // Initialize IV states and store params for regeneration
      setCurrentIV(params.iv);
      setFutureIV(params.iv);
      setCalculationParams(params);
    } catch (error) {
      console.error("Error generating manual data:", error);
      setOptionsData(null);
    } finally {
      setLoading(false);
    }
  };

  // TODO: Uncomment when API is ready
  // Handle mode change
  /* eslint-disable no-unused-vars */
  const handleModeChange = (newMode) => {
    setMode(newMode);
    setOptionsData(null);
    setSelectedExpiration(null);
    setSelectedStrike(null);

    if (newMode === "api") {
      // Reset to API mode
      if (selectedSymbol) {
        fetchOptionsData(selectedSymbol);
      }
    }
  };
  /* eslint-enable no-unused-vars */

  // Load data when symbol changes (API mode only)
  useEffect(() => {
    if (mode === "api" && selectedSymbol) {
      fetchOptionsData(selectedSymbol);
    }
  }, [mode, selectedSymbol, fetchOptionsData]);

  // Set default strike when strikes are available
  useEffect(() => {
    if (strikes.length > 0 && !selectedStrike) {
      setSelectedStrike(strikes[0].strike);
    }
  }, [strikes, selectedStrike]);

  // Regenerate data when futureIV changes
  useEffect(() => {
    if (
      mode === "manual" &&
      calculationParams &&
      futureIV !== null &&
      futureIV !== calculationParams.iv
    ) {
      const data = generateManualOptionsData({
        ...calculationParams,
        futureIV,
      });
      setOptionsData(data);
    }
  }, [futureIV, calculationParams, mode]);

  // Set page title
  useEffect(() => {
    const symbolPart = selectedSymbol ? ` - ${selectedSymbol}` : "";
    document.title = `Options Simulator${symbolPart} | Dindicator Dashboard`;
  }, [selectedSymbol]);

  return (
    <div style={{ padding: "24px", minHeight: "100vh" }}>
      <Title level={2}>
        <ThunderboltOutlined /> Options Simulator - Risk/Reward Analyzer
      </Title>

      <Card style={{ marginBottom: 24 }}>
        {/* TODO: Uncomment when API is ready
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24}>
            <Segmented
              value={mode}
              onChange={handleModeChange}
              options={[
                {
                  label: "API Mode",
                  value: "api",
                  icon: <ApiOutlined />,
                },
                {
                  label: "Manual Calculation",
                  value: "manual",
                  icon: <CalculatorOutlined />,
                },
              ]}
              block
            />
          </Col>
        </Row>
        */}

        {mode === "api" ? (
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <SymbolSelector
                selectedSymbol={selectedSymbol}
                onSymbolChange={handleSymbolChange}
                disabled={loading}
              />
            </Col>
          </Row>
        ) : (
          <ManualInputForm
            onCalculate={handleManualCalculate}
            loading={loading}
            onIVChange={(newIV) => {
              setCurrentIV(newIV);
              setFutureIV(newIV);
            }}
          />
        )}
      </Card>

      {loading && (
        <Card style={{ textAlign: "center", padding: "48px" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            {mode === "manual"
              ? "Calculating options..."
              : "Loading options data..."}
          </div>
        </Card>
      )}

      {mode === "api" && !selectedSymbol && !loading && <EmptyState />}

      {((mode === "api" && selectedSymbol && !loading && optionsData) ||
        (mode === "manual" && !loading && optionsData)) && (
        <>
          <ControlsCard
            expirations={expirations}
            selectedExpiration={selectedExpiration}
            setSelectedExpiration={handleExpirationChange}
            expectedMove={expectedMove}
            setExpectedMove={setExpectedMove}
            expectedDays={expectedDays}
            setExpectedDays={setExpectedDays}
            currentExpirationData={currentExpirationData}
            currentIV={currentIV}
            futureIV={futureIV}
            setFutureIV={setFutureIV}
          />

          <BestOptionAlert
            bestOption={bestOption}
            expectedDays={expectedDays}
            expectedMove={expectedMove}
          />

          <Tabs
            defaultActiveKey="1"
            items={[
              {
                key: "1",
                label: "📊 Complete Overview",
                children: (
                  <CompleteOverviewTab
                    comprehensiveMatrixData={comprehensiveMatrixData}
                    heatmapScale={heatmapScale}
                    allTradingDays={allTradingDays}
                    expectedMove={expectedMove}
                    calculateBestOptions={calculateBestOptions}
                    selectedStrike={selectedStrike}
                    setSelectedStrike={setSelectedStrike}
                    multiStrikeTimeDecayData={multiStrikeTimeDecayData}
                    strikes={strikes}
                    strikeComparisonData={strikeComparisonData}
                    bestOption={bestOption}
                  />
                ),
              },
              {
                key: "2",
                label: "🏆 Robust Rankings",
                children: (
                  <RobustRankingsTab
                    calculateBestOptions={calculateBestOptions}
                    expectedDays={expectedDays}
                    expectedMove={expectedMove}
                    setSelectedStrike={setSelectedStrike}
                    selectedStrike={selectedStrike}
                  />
                ),
              },
              {
                key: "3",
                label: "⚠️ Risk Analysis",
                children: (
                  <RiskAnalysisTab
                    riskAnalysisData={riskAnalysisData}
                    profitLossChartData={profitLossChartData}
                    selectedStrike={selectedStrike}
                    setSelectedStrike={setSelectedStrike}
                    strikes={strikes}
                    expectedMove={expectedMove}
                  />
                ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
};

export default OptionSimulator;
